import React, { Fragment } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  ThemeProvider,
  TextField,
  Box,
  Button,
} from "@mui/material";
import {
  AddOutlined,
  DeleteOutlined,
  EditOutlined,
  Check as CheckIcon,
  Clear as ClearIcon,
} from "@mui/icons-material";
import { tableTheme } from "mui-theme";

interface CriteriaLookup {
  [key: string]: string;
}

const criteriaLookup: CriteriaLookup = {
  leftToSell: "left to sell",
};

interface CriteriaRow {
  rateBand: number;
  criteria: string;
  min: number;
  max: number;
  isEditing?: boolean;
}

interface HotelCriteria {
  min: number;
  max: number;
}

interface BandCriterias {
  [key: string]: HotelCriteria;
}

interface Band {
  criterias: BandCriterias;
}

interface HotelProps {
  suggestionParams: {
    matrixes: Array<{
      bands: Band[];
    }>;
  };
}

interface State {
  data: CriteriaRow[];
  hotel: HotelProps;
  maxRateBandId: number;
}

export default class CriteriaMatrix extends React.Component<
  { hotel: HotelProps },
  State
> {
  state: State = {
    data: [],
    hotel: {} as HotelProps,
    maxRateBandId: 0,
  };

  constructor(props: { hotel: HotelProps }) {
    super(props);
    this.state.hotel = props.hotel;
    this.state.data = this.formatData(props.hotel);
    let maxRateBandId = 0;
    for (let row of this.state.data) {
      if (row.rateBand > maxRateBandId) {
        maxRateBandId = row.rateBand;
      }
    }
    this.state.maxRateBandId = maxRateBandId;
  }

  formatData(hotel: HotelProps): CriteriaRow[] {
    let res: CriteriaRow[] = [];
    hotel.suggestionParams.matrixes[0].bands.forEach(
      (band: Band, index: number) => {
        Object.keys(band.criterias).forEach((name) => {
          res.push({
            rateBand: index + 1,
            criteria: name,
            min: band.criterias[name].min,
            max: band.criterias[name].max,
          });
        });
      },
    );
    return res;
  }

  handleAdd = () => {
    const newRow: CriteriaRow = {
      rateBand: this.state.maxRateBandId + 1,
      criteria: "leftToSell",
      min: 0,
      max: 0,
      isEditing: true,
    };
    this.setState((prev) => ({
      data: [...prev.data, newRow],
      maxRateBandId: prev.maxRateBandId + 1,
    }));
  };

  handleEdit = (index: number) => {
    const newData = [...this.state.data];
    newData[index] = { ...newData[index], isEditing: true };
    this.setState({ data: newData });
  };

  handleSave = (index: number) => {
    const row = this.state.data[index];
    if (row.min < 0) {
      alert("Negative number is not allowed");
      return;
    }
    if (row.max < 0) {
      alert("Negative number is not allowed");
      return;
    }
    if (row.max <= row.min) {
      alert("Max can't be less than or equal to Min");
      return;
    }

    const newData = [...this.state.data];
    newData[index] = { ...row, isEditing: false };
    this.setState({ data: newData });
  };

  handleCancel = (index: number) => {
    const newData = [...this.state.data];
    if (newData[index].rateBand === this.state.maxRateBandId) {
      // If it's a new row, remove it
      newData.splice(index, 1);
      this.setState((prev) => ({
        data: newData,
        maxRateBandId: prev.maxRateBandId - 1,
      }));
    } else {
      // Otherwise just cancel editing
      newData[index] = { ...newData[index], isEditing: false };
      this.setState({ data: newData });
    }
  };

  handleDelete = (index: number) => {
    const newData = [...this.state.data];
    newData.splice(index, 1);
    this.setState({ data: newData });
  };

  handleChange = (
    index: number,
    field: keyof CriteriaRow,
    value: string | number,
  ) => {
    const newData = [...this.state.data];
    newData[index] = { ...newData[index], [field]: value };
    this.setState({ data: newData });
  };

  exportCriterias() {
    const res: { [key: number]: { [key: string]: HotelCriteria } } = [];
    for (let item of this.state.data) {
      res[item.rateBand - 1] = {};
      res[item.rateBand - 1][item.criteria] = {
        min: item.min,
        max: item.max,
      };
    }
    return res;
  }

  render() {
    return (
      <Fragment>
        <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}>
          <Button
            color="info"
            variant="contained"
            startIcon={<AddOutlined />}
            onClick={this.handleAdd}
            id="add-rate-band"
          >
            Add Rate Band
          </Button>
        </Box>
        <ThemeProvider theme={tableTheme}>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Rate band</TableCell>
                  <TableCell>Criteria</TableCell>
                  <TableCell>Min</TableCell>
                  <TableCell>Max</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {this.state.data.map((row, index) => (
                  <TableRow key={index}>
                    <TableCell>{row.rateBand}</TableCell>
                    <TableCell>
                      {row.isEditing ? (
                        <TextField
                          select
                          fullWidth
                          value={row.criteria}
                          onChange={(e) =>
                            this.handleChange(index, "criteria", e.target.value)
                          }
                          SelectProps={{
                            native: true,
                          }}
                        >
                          {Object.entries(criteriaLookup).map(
                            ([value, label]) => (
                              <option key={value} value={value}>
                                {label}
                              </option>
                            ),
                          )}
                        </TextField>
                      ) : (
                        criteriaLookup[row.criteria]
                      )}
                    </TableCell>
                    <TableCell>
                      {row.isEditing ? (
                        <TextField
                          type="number"
                          value={row.min}
                          onChange={(e) =>
                            this.handleChange(
                              index,
                              "min",
                              Number(e.target.value),
                            )
                          }
                        />
                      ) : (
                        row.min
                      )}
                    </TableCell>
                    <TableCell>
                      {row.isEditing ? (
                        <TextField
                          type="number"
                          value={row.max}
                          onChange={(e) =>
                            this.handleChange(
                              index,
                              "max",
                              Number(e.target.value),
                            )
                          }
                        />
                      ) : (
                        row.max
                      )}
                    </TableCell>
                    <TableCell>
                      {row.isEditing ? (
                        <>
                          <IconButton
                            onClick={() => this.handleSave(index)}
                            size="small"
                            color="primary"
                          >
                            <CheckIcon />
                          </IconButton>
                          <IconButton
                            onClick={() => this.handleCancel(index)}
                            size="small"
                            color="error"
                          >
                            <ClearIcon />
                          </IconButton>
                        </>
                      ) : (
                        <>
                          <IconButton
                            onClick={() => this.handleEdit(index)}
                            size="small"
                            color="primary"
                          >
                            <EditOutlined />
                          </IconButton>
                          <IconButton
                            onClick={() => this.handleDelete(index)}
                            size="small"
                            color="error"
                          >
                            <DeleteOutlined />
                          </IconButton>
                        </>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </ThemeProvider>
      </Fragment>
    );
  }
}
